import React, { useCallback, useEffect, useState } from "react";
import "./style.scss";
import GoBack from "../../../assets/GoBackApp.svg";
import { useNavigate, useParams } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import { ActionBtn } from "../components/action-btn";
import { useStore } from "zustand";
import { marketStore } from "entities/market";
import { AssetType, OperationType } from "static/types";
import { DealsTable, MarketChart } from "widgets/market";
import AssetIcon from "../components/asset-icon";
import useCanAction from "hooks/useCanAction";
import HorizontalScroll from "../components/horizontal-scroll";
import { TabBtn } from "../components/tab-btn";
import DepthChart from "../../../assets/DepthChart.png";
import DepthLines from "../../../assets/DepthLines.png";
import DepthField from "../components/depth-field";
import { useTranslation } from "react-i18next";
import { generalSocketStore } from "services/general.socket";
import SaveToFavBtn from "../components/save-to-favorites-btn";
import useSaveHandler from "hooks/useSaveToFavorites";
import SavedToFavNotification from "../components/saved-to-favorites-notification";
import usePrice from "hooks/usePrice";

export const CurrencyDetailsPage = () => {
  const { t } = useTranslation("app");
  const [activeTab, setActiveTab] = useState("Depth");
  const { asset_id } = useParams();

  const {
    isInitialLoading,
    assetInfo,
    loadAssetInfo,
    loadAssetChart,
    assetDepth,
    addFavorites,
    removeFavorite,
  } = useStore(marketStore);

  const { connected, socket } = useStore(generalSocketStore);

  const action = useCanAction();

  const symbol = assetInfo?.asset?.symbol || "";
  const currSymbol = assetInfo?.asset?.currency.symbol || "";

  const { ask, bid, mid, change, changePercent } = usePrice({
    assetType: AssetType.Currency,
    ask: assetInfo?.asset?.ask,
    bid: assetInfo?.asset?.bid,
    price: assetInfo?.asset?.price,
    change: assetInfo?.asset?.change,
    useAssetDepth: true,
  });

  const getAssetInfo = useCallback(() => {
    try {
      const info = assetInfo?.info;
      return info;
    } catch (error) {
      return null;
    }
  }, [assetInfo]);

  useEffect(() => {
    loadAssetInfo(asset_id);
    loadAssetChart(asset_id);
    // loadAssetDepth(asset_id);
  }, []);

  useEffect(() => {
    if (!connected || !asset_id) {
      socket.off("update-orderbook");
      return;
    }
    socket.on("update-orderbook", (data) => {
      const { asset_id: as_id, orderbook } = data;
      if (as_id == +asset_id) {
        marketStore.setState({ assetDepth: orderbook });
      }
    });
    socket.emit("subscribe-orderbook", { asset_id });

    return () => {
      socket
        .emit("unsubscribe-orderbook", { asset_id })
        .off("update-orderbook");
    };
  }, [asset_id, connected]);

  const navigate = useNavigate();
  const handleNavigate = () => {
    navigate(-1);
  };

  const handleDeal = (type: number, operation: string) => {
    if (operation === "buy") {
      navigate(PATH_LIST.buy(asset_id!) + `?type=${type}`);
    } else {
      navigate(PATH_LIST.sell(asset_id!) + `?type=${type}`);
    }
  };

  const { saved, showNotification, handleSave } = useSaveHandler(
    +asset_id!,
    addFavorites,
    removeFavorite
  );

  if (isInitialLoading || !assetInfo || !assetInfo.asset) {
    return (
      <div className={`stock-details stock-details${activeTab}`}>
        <div className="stock-details-header">
          <img
            className="go-back-portfolio"
            onClick={handleNavigate}
            src={GoBack}
            alt="Go Back"
          />
          <div>
            <p>{symbol}</p>
          </div>
        </div>
        <div className="stock-details-main">
          <p className="assets-loading assets-loading-page">{t("loading")}</p>
        </div>
      </div>
    );
  }

  const scroll = () => {
    const stockDetails = document.querySelector(".stock-details");
    if (stockDetails) {
      stockDetails.scrollTop = 0;
    }
  };
  const handleTabClick = (text: string) => {
    setActiveTab(text);
    scroll();
  };

  return (
    <div
      className={`stock-details stock-details-currency stock-details${activeTab}`}
    >
      {showNotification && <SavedToFavNotification symbol={symbol} />}

      <div className="stock-details-header">
        <img
          className="go-back-portfolio"
          onClick={handleNavigate}
          src={GoBack}
          alt="Go Back"
        />
        <div className="stock-details-header-info stock-details-header-info-currency">
          <AssetIcon
            asset={{
              symbol: symbol!,
              type: AssetType.Currency,
              currency: assetInfo?.asset?.currency,
            }}
          />
          <p>
            {symbol} <span>{getAssetInfo()?.companyName}</span>
          </p>
        </div>
      </div>

      <HorizontalScroll className="tab-bar">
        <TabBtn
          text={t("trading")}
          active={activeTab === "Trading"}
          onClick={() => handleTabClick("Trading")}
        />
        <TabBtn
          text={t("depth")}
          active={activeTab === "Depth"}
          onClick={() => handleTabClick("Depth")}
        />
      </HorizontalScroll>

      {activeTab === "Trading" ? (
        <div className="stock-details-main">
          <div className="currency-info-bargaining">
            <p className="price">
              {mid} {currSymbol}
            </p>
            {change && (
              <>
                <div className="gain-percent">
                  <span className={change < 0 ? "loss" : ""}>
                    {change} {currSymbol}
                  </span>
                  <span
                    className={"gains-percent " + (change < 0 ? "loss" : "")}
                  >
                    {changePercent}%
                  </span>
                </div>
                <p>{t("today")}</p>
              </>
            )}
          </div>
          <MarketChart onlyLineChart={true} />
          <DealsTable assetId={assetInfo?.asset?.id || undefined} />
        </div>
      ) : (
        <div className="stock-details-main stock-details-main-review">
          <div className="depth-header">
            <p>{t("depthOfMarket")}</p>

            {/* <div>
              <button>
                <img src={DepthLines} alt="depth-lines" />
              </button>
              <button>
                <img src={DepthChart} alt="depth-chart" />
              </button>
            </div> */}
          </div>

          <div className="depth-main">
            <div className="depth-main-header">
              <p>{t("purchase")}</p>
              <p>{t("price")}</p>
              <p>{t("sale")}</p>
            </div>
            <div className="depth-content">
              {assetDepth.map((item, index) => {
                return (
                  <DepthField
                    key={index}
                    purchase={
                      item.operation === "sell" ? item.quantity : undefined
                    }
                    price={item.price + currSymbol}
                    sale={item.operation === "buy" ? item.quantity : undefined}
                    progress={item.progress}
                    currency={""}
                    onClick={() => {
                      navigate(
                        PATH_LIST[item.operation](asset_id) +
                          `?type=${assetInfo!.asset!.type}&price=${item.price}`
                      );
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="action-btns-currency-wrapper">
        <ActionBtn
          onClick={() =>
            action(
              () => handleDeal(assetInfo!.asset!.type, OperationType.Buy),
              "fx"
            )
          }
          action={t("buy")}
          price={ask}
          currency={currSymbol}
          type="green"
          disabled={false}
          customClass
        />

        <ActionBtn
          onClick={() =>
            action(
              () => handleDeal(assetInfo!.asset!.type, OperationType.Sell),
              "fx"
            )
          }
          action={t("sell")}
          price={bid}
          currency={currSymbol}
          type="red"
          disabled={false}
          customClass
        />
      </div>
    </div>
  );
};
